import { type FormEvent, useState } from "react";
import { Link } from "react-router";

import {
  type FamilyRow,
  type RecipeException,
  type RecipeVersion,
  listExceptions,
  listFamilies,
  listVersions,
  message,
} from "./recipesClient";

/** Document families of an engagement, and the recipe versions of the family in view. */
export function RecipesPage() {
  const [families, setFamilies] = useState<FamilyRow[] | null>(null);
  const [versions, setVersions] = useState<RecipeVersion[] | null>(null);
  const [exceptions, setExceptions] = useState<RecipeException[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function onFind(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const engagement = String(new FormData(event.currentTarget).get("engagement") ?? "").trim();
    setVersions(null);
    setExceptions(null);
    listFamilies(engagement)
      .then((rows) => {
        setFamilies(rows);
        setError(null);
      })
      .catch((caught: unknown) => setError(message(caught)));
  }

  function showVersions(familyId: string) {
    Promise.all([listVersions(familyId), listExceptions(familyId)])
      .then(([rows, routed]) => {
        setVersions(rows);
        setExceptions(routed);
        setError(null);
      })
      .catch((caught: unknown) => setError(message(caught)));
  }

  return (
    <section aria-labelledby="recipes-title">
      <h1 id="recipes-title">Extraction recipes</h1>
      <form onSubmit={onFind} aria-label="Find families">
        <label>
          Engagement id <input name="engagement" required />
        </label>
        <button type="submit">Show families</button>
      </form>
      {error && <p role="alert">Could not load: {error}</p>}
      {families?.length === 0 && <p>This engagement has no document families.</p>}
      {families && families.length > 0 && (
        <ul aria-label="Families">
          {families.map((family) => (
            <li key={family.id}>
              {family.name} ({family.family_state}){" "}
              <button type="button" onClick={() => showVersions(family.id)}>
                Recipes for {family.family_key}
              </button>
            </li>
          ))}
        </ul>
      )}
      {versions?.length === 0 && <p>This family has no recipe yet.</p>}
      {versions && versions.length > 0 && (
        <ul aria-label="Recipe versions">
          {versions.map((version) => (
            <li key={version.id}>
              <Link to={`/recipes/${version.id}`}>Version {version.version}</Link>:{" "}
              {version.lifecycle_state}, {version.fields.length} fields
            </li>
          ))}
        </ul>
      )}
      {exceptions?.length === 0 && <p>This family has no exceptions.</p>}
      {exceptions && exceptions.length > 0 && (
        <table aria-label="Exceptions">
          <thead>
            <tr>
              <th>File version</th>
              <th>Kind</th>
              <th>Reasons</th>
              <th>Closest other recipe</th>
            </tr>
          </thead>
          <tbody>
            {exceptions.map((item) => (
              <tr key={item.id}>
                <td>{item.source_artifact_version_id}</td>
                <td>{item.exception_kind}</td>
                <td>
                  <ul>
                    {item.reasons.map((reason) => (
                      <li key={`${reason.code}-${reason.field_name ?? ""}`}>{reason.detail}</li>
                    ))}
                  </ul>
                </td>
                <td>
                  {item.closest_recipe_version_id ? (
                    <Link to={`/recipes/${item.closest_recipe_version_id}`}>
                      Fit score {String(item.closest_fit_score)}
                    </Link>
                  ) : (
                    item.no_closest_statement
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
